import { api, getEmail, getDisplayName, ApiError, UserTarget, WeightEntry, DowTargetOverride, renderError } from "../api.js";
import { applyTheme } from "../main.js";

export async function renderSettings (root: HTMLElement): Promise<void> {
    root.appendChild (await targetsCard ());
    root.appendChild (weightCard ());
    root.appendChild (themeCard ());
    root.appendChild (pushCard ());
    root.appendChild (changePasswordCard ());
    root.appendChild (membersCard ());
}

function weightCard (): HTMLElement {
    const card = document.createElement ("sl-card");
    const wrap = document.createElement ("div");
    wrap.className = "stack";

    const title = document.createElement ("h3");
    title.textContent = "Gewicht";
    title.style.margin = "0";

    const hint = document.createElement ("div");
    hint.className = "muted";
    hint.textContent = "Manuell eingeben oder via Garmin-Sync (tools/garmin_sync.py).";

    const inputRow = document.createElement ("div");
    inputRow.className = "row";

    const weight = document.createElement ("sl-input");
    weight.setAttribute ("label", "Gewicht (kg)");
    weight.setAttribute ("type", "number");
    weight.setAttribute ("step", "0.1");
    weight.style.flex = "1 1 auto";

    const bf = document.createElement ("sl-input");
    bf.setAttribute ("label", "Koerperfett (%) — optional");
    bf.setAttribute ("type", "number");
    bf.setAttribute ("step", "0.1");
    bf.style.flex = "1 1 auto";

    const save = document.createElement ("sl-button");
    save.setAttribute ("variant", "primary");
    save.textContent = "Eintragen";

    const status = document.createElement ("div");

    const chart = document.createElement ("div");
    chart.style.marginTop = "0.5rem";

    save.addEventListener ("click", async () => {
        const val = (el: HTMLElement): string => (el as unknown as { value: string }).value;
        const w = parseFloat (val (weight));
        if (!Number.isFinite (w) || w <= 0) {
            status.className = "error";
            status.textContent = "Gueltiges Gewicht eintragen.";
            return;
        }
        const payload: { weight_kg: number; body_fat_pct?: number; source?: string } = {
            weight_kg: w,
            source: "manual",
        };
        const bfv = parseFloat (val (bf));
        if (Number.isFinite (bfv) && bfv > 0) payload.body_fat_pct = bfv;

        save.setAttribute ("loading", "");
        try {
            await api.postWeight (payload);
            status.className = "muted";
            status.textContent = "Eingetragen.";
            (weight as unknown as { value: string }).value = "";
            (bf as unknown as { value: string }).value = "";
            await reloadChart ();
        }
        catch (err) {
            status.className = "error";
            status.textContent = (err instanceof Error) ? err.message : "Fehler";
        }
        finally {
            save.removeAttribute ("loading");
        }
    });

    async function reloadChart (): Promise<void> {
        chart.innerHTML = "<sl-spinner></sl-spinner>";
        try {
            const to = Math.floor (Date.now () / 1000);
            const from = to - 90 * 86400;
            const entries: WeightEntry[] = await api.listWeight (from, to);
            chart.innerHTML = "";
            if (entries.length === 0) {
                const empty = document.createElement ("div");
                empty.className = "muted";
                empty.textContent = "Noch keine Gewichtsdaten.";
                chart.appendChild (empty);
                return;
            }
            // Mini-Linien-Chart in SVG.
            const W = 600, H = 120, padL = 30, padR = 5, padT = 8, padB = 18;
            const xs = entries.map (e => e.weighed_at);
            const ys = entries.map (e => e.weight_kg);
            const xMin = xs[0];
            const xMax = xs[xs.length - 1];
            const xSpan = Math.max (1, xMax - xMin);
            const yMin = Math.min (...ys) - 0.5;
            const yMax = Math.max (...ys) + 0.5;
            const ySpan = Math.max (0.1, yMax - yMin);

            const px = (t: number) => padL + ((t - xMin) / xSpan) * (W - padL - padR);
            const py = (w: number) => padT + (1 - (w - yMin) / ySpan) * (H - padT - padB);

            let svg = `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" `
                + `style="width:100%;height:${H}px;background:var(--sl-color-neutral-100,#f3f4f6);border-radius:4px;">`;
            // Linie
            let pts = "";
            for (const e of entries) pts += `${px (e.weighed_at).toFixed (1)},${py (e.weight_kg).toFixed (1)} `;
            svg += `<polyline points="${pts}" fill="none" stroke="var(--sl-color-primary-500,#3b82f6)" stroke-width="2"/>`;
            // Punkte
            for (const e of entries) {
                svg += `<circle cx="${px (e.weighed_at).toFixed (1)}" cy="${py (e.weight_kg).toFixed (1)}" r="2.5" fill="var(--sl-color-primary-600,#2563eb)"><title>${e.weight_kg.toFixed (1)} kg @ ${new Date (e.weighed_at * 1000).toLocaleDateString ("de-DE")}</title></circle>`;
            }
            // Y-Achsen-Beschriftung
            svg += `<text x="2" y="${(padT + 4).toFixed (1)}" font-size="9" fill="var(--sl-color-neutral-500,#6b7280)">${yMax.toFixed (1)}</text>`;
            svg += `<text x="2" y="${(H - padB + 4).toFixed (1)}" font-size="9" fill="var(--sl-color-neutral-500,#6b7280)">${yMin.toFixed (1)}</text>`;
            svg += "</svg>";

            const c = document.createElement ("div");
            c.innerHTML = svg;
            chart.appendChild (c);

            const last = entries[entries.length - 1];
            const lastInfo = document.createElement ("div");
            lastInfo.className = "muted";
            lastInfo.style.fontSize = "0.75rem";
            lastInfo.style.marginTop = "0.25rem";
            lastInfo.textContent = `Letzter Wert: ${last.weight_kg.toFixed (1)} kg`
                + (last.body_fat_pct !== null ? ` / ${last.body_fat_pct.toFixed (1)} % KFA` : "")
                + ` (${new Date (last.weighed_at * 1000).toLocaleDateString ("de-DE")}, ${last.source})`;
            chart.appendChild (lastInfo);
        }
        catch (err) {
            renderError (chart, err);
        }
    }
    void reloadChart ();

    inputRow.appendChild (weight);
    inputRow.appendChild (bf);
    inputRow.appendChild (save);
    [title, hint, inputRow, status, chart].forEach (el => wrap.appendChild (el));
    card.appendChild (wrap);
    return card;
}

function themeCard (): HTMLElement {
    const card = document.createElement ("sl-card");
    const wrap = document.createElement ("div");
    wrap.className = "stack";

    const title = document.createElement ("h3");
    title.textContent = "Darstellung";
    title.style.margin = "0";

    const hint = document.createElement ("div");
    hint.className = "muted";
    hint.textContent = "Folgt standardmaessig dem System; manuelle Auswahl ueberschreibt das.";

    const sw = document.createElement ("sl-switch");
    sw.textContent = "Dark Mode";
    const stored = localStorage.getItem ("trackme.theme");
    const wantDark = stored === "dark"
        || (stored === null && window.matchMedia ("(prefers-color-scheme: dark)").matches);
    if (wantDark) sw.setAttribute ("checked", "");

    const reset = document.createElement ("sl-button");
    reset.setAttribute ("size", "small");
    reset.setAttribute ("variant", "text");
    reset.textContent = "An Systemeinstellung koppeln";
    reset.addEventListener ("click", () => {
        localStorage.removeItem ("trackme.theme");
        applyTheme ();
        const sys = window.matchMedia ("(prefers-color-scheme: dark)").matches;
        if (sys) sw.setAttribute ("checked", ""); else sw.removeAttribute ("checked");
    });

    sw.addEventListener ("sl-change", () => {
        const dark = (sw as unknown as { checked: boolean }).checked;
        localStorage.setItem ("trackme.theme", dark ? "dark" : "light");
        applyTheme ();
    });

    [title, hint, sw, reset].forEach (el => wrap.appendChild (el));
    card.appendChild (wrap);
    return card;
}

function pushCard (): HTMLElement {
    const card = document.createElement ("sl-card");
    const wrap = document.createElement ("div");
    wrap.className = "stack";

    const title = document.createElement ("h3");
    title.textContent = "Push-Benachrichtigungen";
    title.style.margin = "0";

    const hint = document.createElement ("div");
    hint.className = "muted";
    hint.textContent = "Setzt eine installierte PWA voraus (iOS 16.4+). Der Server-VAPID-Key muss in der .env gesetzt sein.";

    const sw = document.createElement ("sl-switch");
    sw.textContent = "Aktivieren";

    const status = document.createElement ("div");
    status.className = "muted";
    status.style.fontSize = "0.875rem";

    // Initial-Status: pruefen, ob bereits subscribed.
    void (async () => {
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
            status.textContent = "Browser unterstuetzt Web-Push nicht.";
            sw.setAttribute ("disabled", "");
            return;
        }
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription ();
            if (sub !== null) sw.setAttribute ("checked", "");
        }
        catch { /* ignore */ }
    }) ();

    sw.addEventListener ("sl-change", async () => {
        const checked = (sw as unknown as { checked: boolean }).checked;
        status.className = "muted";

        try {
            const reg = await navigator.serviceWorker.ready;
            const existing = await reg.pushManager.getSubscription ();

            if (checked) {
                // Public Key vom Server holen
                const keyRes = await fetch ("/api/push/vapid-public-key");
                const keyData = await keyRes.json () as { key: string };
                if (keyData.key === "") {
                    status.className = "error";
                    status.textContent = "Server hat keinen VAPID-Key gesetzt.";
                    sw.removeAttribute ("checked");
                    return;
                }
                // Subscribe
                const appServerKey = base64UrlToBytes (keyData.key);
                const sub = existing ?? await reg.pushManager.subscribe ({
                    userVisibleOnly: true,
                    applicationServerKey: appServerKey,
                });
                const json = sub.toJSON () as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
                await api.pushSubscribe ({
                    endpoint: json.endpoint ?? "",
                    p256dh: json.keys?.p256dh ?? "",
                    auth: json.keys?.auth ?? "",
                });
                status.textContent = "Aktiviert.";
            }
            else {
                if (existing !== null) {
                    const json = existing.toJSON () as { endpoint?: string };
                    try { await api.pushUnsubscribe (json.endpoint ?? ""); } catch { /* ignore */ }
                    await existing.unsubscribe ();
                }
                status.textContent = "Deaktiviert.";
            }
        }
        catch (err) {
            status.className = "error";
            status.textContent = (err instanceof Error) ? err.message : "Fehler";
            // Toggle wieder zurueck
            if (checked) sw.removeAttribute ("checked"); else sw.setAttribute ("checked", "");
        }
    });

    [title, hint, sw, status].forEach (el => wrap.appendChild (el));
    card.appendChild (wrap);
    return card;
}

function base64UrlToBytes (s: string): Uint8Array {
    const pad = (4 - (s.length % 4)) % 4;
    const b64 = (s + "====".slice (0, pad)).replace (/-/g, "+").replace (/_/g, "/");
    const bin = atob (b64);
    const out = new Uint8Array (bin.length);
    for (let i = 0; i < bin.length; ++i) out[i] = bin.charCodeAt (i);
    return out;
}

async function targetsCard (): Promise<HTMLElement> {
    const card = document.createElement ("sl-card");
    const wrap = document.createElement ("div");
    wrap.className = "stack";

    const title = document.createElement ("h3");
    title.textContent = "Tagesziele";
    title.style.margin = "0";

    const hint = document.createElement ("div");
    hint.className = "muted";
    hint.textContent = "Default gilt fuer Tage ohne Override. Pro Wochentag laesst sich abweichen.";

    let current: UserTarget | null = null;

    try {
        current = await api.getTarget ();
    }
    catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) {
            const e = document.createElement ("div");
            e.className = "error";
            e.textContent = (err instanceof Error) ? err.message : "Fehler";
            wrap.appendChild (e);
        }
    }

    const defaults = {
        kcal: current?.kcal_target ?? 2000,
        protein: current?.protein_target ?? 50,
        fat: current?.fat_target ?? 70,
        sugar: current?.sugar_target ?? 50,
    };

    const kcal = mkNumberInput ("Kalorien (kcal)", defaults.kcal);
    const protein = mkNumberInput ("Eiweiss (g)", defaults.protein);
    const fat = mkNumberInput ("Fett (g)", defaults.fat);
    const sugar = mkNumberInput ("Zucker (g)", defaults.sugar);

    const msg = document.createElement ("div");

    const save = document.createElement ("sl-button");
    save.setAttribute ("variant", "primary");
    save.textContent = "Speichern (Default)";
    save.addEventListener ("click", async () => {
        const num = (el: HTMLElement): number => parseFloat ((el as unknown as { value: string }).value) || 0;
        msg.className = "";
        msg.textContent = "";
        save.setAttribute ("loading", "");
        try {
            await api.putTarget ({
                kcal_target: Math.round (num (kcal)),
                protein_target: num (protein),
                fat_target: num (fat),
                sugar_target: num (sugar),
            });
            msg.className = "muted";
            msg.textContent = "Gespeichert.";
        }
        catch (err) {
            msg.className = "error";
            msg.textContent = (err instanceof Error) ? err.message : "Fehler";
        }
        finally {
            save.removeAttribute ("loading");
        }
    });

    [title, hint, kcal, protein, fat, sugar, msg, save].forEach (el => wrap.appendChild (el));

    // Per-Wochentag-Overrides.
    const dowDivider = document.createElement ("sl-divider");
    dowDivider.style.margin = "0.75rem 0 0.5rem 0";
    wrap.appendChild (dowDivider);

    const dowTitle = document.createElement ("div");
    dowTitle.style.fontWeight = "600";
    dowTitle.textContent = "Abweichende Wochentag-Ziele";
    wrap.appendChild (dowTitle);

    const dowHint = document.createElement ("div");
    dowHint.className = "muted";
    dowHint.style.fontSize = "0.75rem";
    dowHint.textContent = "Ohne Override gilt der Default oben. Trash-Icon entfernt den Override.";
    wrap.appendChild (dowHint);

    const dowNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
    const overrides = new Map<number, DowTargetOverride> ();
    for (const o of (current?.dow_overrides ?? [])) overrides.set (o.dow, o);

    for (let dow = 0; dow < 7; ++dow) {
        const row = document.createElement ("div");
        row.style.display = "grid";
        row.style.gridTemplateColumns = "2.5rem repeat(4, 1fr) auto auto";
        row.style.gap = "0.4rem";
        row.style.alignItems = "end";
        row.style.marginTop = "0.4rem";

        const label = document.createElement ("strong");
        label.textContent = dowNames[dow];
        label.style.alignSelf = "center";
        row.appendChild (label);

        const o = overrides.get (dow);
        const kcalIn = mkNumberInput ("kcal", o?.kcal_target ?? defaults.kcal);
        const protIn = mkNumberInput ("Eiwei.", o?.protein_target ?? defaults.protein);
        const fatIn = mkNumberInput ("Fett", o?.fat_target ?? defaults.fat);
        const sugarIn = mkNumberInput ("Zucker", o?.sugar_target ?? defaults.sugar);
        // kompakter
        for (const el of [kcalIn, protIn, fatIn, sugarIn]) {
            el.setAttribute ("size", "small");
        }
        row.appendChild (kcalIn);
        row.appendChild (protIn);
        row.appendChild (fatIn);
        row.appendChild (sugarIn);

        const saveOne = document.createElement ("sl-icon-button");
        saveOne.setAttribute ("name", "check2");
        saveOne.setAttribute ("label", "Speichern");
        saveOne.addEventListener ("click", async () => {
            const num = (el: HTMLElement): number => parseFloat ((el as unknown as { value: string }).value) || 0;
            saveOne.setAttribute ("loading", "");
            try {
                await api.putTargetDow (dow, {
                    kcal_target: Math.round (num (kcalIn)),
                    protein_target: num (protIn),
                    fat_target: num (fatIn),
                    sugar_target: num (sugarIn),
                });
                msg.className = "muted";
                msg.textContent = `Override ${dowNames[dow]} gespeichert.`;
            }
            catch (err) {
                msg.className = "error";
                msg.textContent = (err instanceof Error) ? err.message : "Fehler";
            }
            finally {
                saveOne.removeAttribute ("loading");
            }
        });
        row.appendChild (saveOne);

        const del = document.createElement ("sl-icon-button");
        del.setAttribute ("name", "trash");
        del.setAttribute ("label", "Override entfernen");
        del.addEventListener ("click", async () => {
            try {
                await api.deleteTargetDow (dow);
                (kcalIn as unknown as { value: string }).value = String (defaults.kcal);
                (protIn as unknown as { value: string }).value = String (defaults.protein);
                (fatIn as unknown as { value: string }).value = String (defaults.fat);
                (sugarIn as unknown as { value: string }).value = String (defaults.sugar);
                msg.className = "muted";
                msg.textContent = `Override ${dowNames[dow]} entfernt.`;
            }
            catch (err) {
                msg.className = "error";
                msg.textContent = (err instanceof Error) ? err.message : "Fehler";
            }
        });
        row.appendChild (del);

        wrap.appendChild (row);
    }

    card.appendChild (wrap);
    return card;
}

function mkNumberInput (label: string, value: number): HTMLElement {
    const i = document.createElement ("sl-input");
    i.setAttribute ("label", label);
    i.setAttribute ("type", "number");
    i.setAttribute ("min", "0");
    i.setAttribute ("value", String (value));
    return i;
}

function changePasswordCard (): HTMLElement {
    const card = document.createElement ("sl-card");

    const wrap = document.createElement ("div");
    wrap.className = "stack";

    const title = document.createElement ("h3");
    title.textContent = "Passwort aendern";
    title.style.margin = "0";

    const hint = document.createElement ("div");
    hint.className = "muted";
    hint.textContent = `Angemeldet als ${getDisplayName () ?? "?"} (${getEmail () ?? "?"})`;

    const cur = document.createElement ("sl-input");
    cur.setAttribute ("label", "Aktuelles Passwort");
    cur.setAttribute ("type", "password");
    cur.setAttribute ("password-toggle", "");
    cur.setAttribute ("autocomplete", "current-password");

    const neu = document.createElement ("sl-input");
    neu.setAttribute ("label", "Neues Passwort (min. 8 Zeichen)");
    neu.setAttribute ("type", "password");
    neu.setAttribute ("password-toggle", "");
    neu.setAttribute ("autocomplete", "new-password");

    const neu2 = document.createElement ("sl-input");
    neu2.setAttribute ("label", "Neues Passwort wiederholen");
    neu2.setAttribute ("type", "password");
    neu2.setAttribute ("password-toggle", "");
    neu2.setAttribute ("autocomplete", "new-password");

    const msg = document.createElement ("div");

    const submit = document.createElement ("sl-button");
    submit.setAttribute ("variant", "primary");
    submit.textContent = "Aendern";
    submit.addEventListener ("click", async () => {
        const val = (el: HTMLElement): string => (el as unknown as { value: string }).value;
        const c = val (cur);
        const n = val (neu);
        const n2 = val (neu2);
        const email = getEmail ();

        msg.className = "";
        msg.textContent = "";

        if (email === null) {
            msg.className = "error";
            msg.textContent = "Keine Email in der Session.";
            return;
        }

        if (c === "" || n === "" || n2 === "") {
            msg.className = "error";
            msg.textContent = "Alle Felder ausfuellen.";
            return;
        }

        if (n !== n2) {
            msg.className = "error";
            msg.textContent = "Neues Passwort und Wiederholung stimmen nicht ueberein.";
            return;
        }

        if (n.length < 8) {
            msg.className = "error";
            msg.textContent = "Neues Passwort muss mindestens 8 Zeichen haben.";
            return;
        }

        submit.setAttribute ("loading", "");

        try {
            await api.changePassword (email, c, n);
            msg.className = "muted";
            msg.textContent = "Passwort geaendert.";
            (cur as unknown as { value: string }).value = "";
            (neu as unknown as { value: string }).value = "";
            (neu2 as unknown as { value: string }).value = "";
        }
        catch (err) {
            msg.className = "error";
            msg.textContent = (err instanceof ApiError) ? err.message : "Fehler";
        }
        finally {
            submit.removeAttribute ("loading");
        }
    });

    [title, hint, cur, neu, neu2, msg, submit].forEach (el => wrap.appendChild (el));
    card.appendChild (wrap);

    return card;
}

function membersCard (): HTMLElement {
    const card = document.createElement ("sl-card");
    card.style.marginTop = "1rem";

    const wrap = document.createElement ("div");
    wrap.className = "stack";

    const headerRow = document.createElement ("div");
    headerRow.className = "row";

    const title = document.createElement ("h3");
    title.textContent = "Familienmitglieder";
    title.style.margin = "0";
    title.style.flex = "1 1 auto";

    const addBtn = document.createElement ("sl-button");
    addBtn.setAttribute ("size", "small");
    addBtn.setAttribute ("variant", "primary");
    addBtn.textContent = "+ Anlegen";

    headerRow.appendChild (title);
    headerRow.appendChild (addBtn);

    const list = document.createElement ("div");
    list.className = "stack";

    const dialog = document.createElement ("sl-dialog");
    dialog.setAttribute ("label", "Neues Familienmitglied");

    wrap.appendChild (headerRow);
    wrap.appendChild (list);
    wrap.appendChild (dialog);
    card.appendChild (wrap);

    async function reload (): Promise<void> {
        list.innerHTML = "";
        const spinner = document.createElement ("sl-spinner");
        list.appendChild (spinner);

        try {
            const users = await api.listUsers ();
            list.innerHTML = "";

            for (const u of users) {
                const row = document.createElement ("div");
                row.className = "row";
                const name = document.createElement ("strong");
                name.textContent = u.display_name;
                name.style.flex = "1 1 auto";
                const email = document.createElement ("span");
                email.className = "muted";
                email.textContent = u.email;
                row.appendChild (name);
                row.appendChild (email);
                list.appendChild (row);
            }
        }
        catch (err) {
            list.innerHTML = "";
            const e = document.createElement ("div");
            e.className = "error";
            e.textContent = (err instanceof Error) ? err.message : "Fehler";
            list.appendChild (e);
        }
    }

    addBtn.addEventListener ("click", () => {
        dialog.innerHTML = "";
        dialog.appendChild (newMemberForm (async () => {
            (dialog as unknown as { hide: () => void }).hide ();
            await reload ();
        }));
        (dialog as unknown as { show: () => void }).show ();
    });

    void reload ();
    return card;
}

function newMemberForm (onCreated: () => Promise<void>): HTMLElement {
    const form = document.createElement ("div");
    form.className = "stack";

    const email = document.createElement ("sl-input");
    email.setAttribute ("label", "Email");
    email.setAttribute ("type", "email");

    const name = document.createElement ("sl-input");
    name.setAttribute ("label", "Anzeigename");

    const pw = document.createElement ("sl-input");
    pw.setAttribute ("label", "Initial-Passwort (min. 8 Zeichen)");
    pw.setAttribute ("type", "password");
    pw.setAttribute ("password-toggle", "");

    const msg = document.createElement ("div");
    msg.className = "error";

    const submit = document.createElement ("sl-button");
    submit.setAttribute ("variant", "primary");
    submit.textContent = "Anlegen";
    submit.addEventListener ("click", async () => {
        const val = (el: HTMLElement): string => (el as unknown as { value: string }).value;
        const e = val (email).trim ();
        const n = val (name).trim ();
        const p = val (pw);

        msg.textContent = "";

        if (e === "" || n === "" || p === "") {
            msg.textContent = "Email, Name und Passwort sind erforderlich.";
            return;
        }

        if (p.length < 8) {
            msg.textContent = "Passwort braucht mindestens 8 Zeichen.";
            return;
        }

        submit.setAttribute ("loading", "");

        try {
            await api.createUser (e, n, p);
            await onCreated ();
        }
        catch (err) {
            msg.textContent = (err instanceof Error) ? err.message : "Fehler";
        }
        finally {
            submit.removeAttribute ("loading");
        }
    });

    [email, name, pw, msg, submit].forEach (el => form.appendChild (el));
    return form;
}
